/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
-----------------------------------------------------------------------------*/

<template>
  <section aria-label="Manage Wordspotting Settings">
    <div v-if="formStatus == READY">
      <!-- Default/Global -->
      <div
        v-if="showGlobalDefaults"
        class="wordspotting-section"
        aria-labelledby="manageWordspotting1"
      >
        <h3 id="manageWordspotting1">
          Currently Using the Default Wordspotting List
        </h3>
        <div
          v-for="phrase in wordspotting.global"
          v-if="getAlgorithm(phrase.algorithm)"
          :key="phrase.algorithm + 'global'"
        >
          <div v-if="algorithms.length > 1">
            <strong>Recording Type: </strong>{{ getAlgorithm(phrase.algorithm) }}<br>
          </div>
          <wordspotting-list :words="phrase.words" />
        </div>
      </div>

      <!-- Org -->
      <div
        v-if="showOrgSettings"
        class="wordspotting-section"
        aria-labelledby="manageWordspotting2"
      >
        <h3 id="manageWordspotting2">
          Wordspotting List for All &quot;{{ orgName }}&quot; Accounts
        </h3>
        <div
          v-for="phrase in wordspotting.org"
          v-if="getAlgorithm(phrase.algorithm)"
          :key="phrase.algorithm + 'org'"
        >
          <div v-if="algorithms.length > 1">
            <strong>Recording Type: </strong>{{ getAlgorithm(phrase.algorithm) }}<br>
          </div>
          <wordspotting-list :words="phrase.words" />
          <div
            v-if="isOrgLevelManager"
            class="edit-wordspotting"
          >
            <button
              class="btn clear-btn edit-wordspotting"
              type="submit"
              @click.prevent="formType = 'org'; algorithm = phrase.algorithm; groupId = null; advanceFrom(READY)"
            >
              Edit Wordspotting
            </button>
            <a
              href="#"
              class="delete-wordspotting"
              @click.prevent="deleteWordspotting('org', phrase.algorithm)"
            >
              Reset Wordspotting
            </a>
          </div>
        </div>
      </div>

      <!-- Group -->
      <div
        v-if="showGroupSettings"
        aria-labelledby="manageWordspotting3"
      >
        <h3 id="manageWordspotting3">
          Wordspotting Lists for Your {{ (orgType == 'edu') ? 'Classes' : 'Groups' }}
        </h3>
        <div
          v-for="group in wordspotting.groups"
          class="wordspotting-section"
        >
          <div
            v-for="phrase in group"
            v-if="getAlgorithm(phrase.algorithm)"
            :key="phrase.algorithm + phrase.groupId"
            class="wordspotting-groups-list"
          >
            <div class="wordspotting-group-name">
              <strong>{{ (orgType == 'edu') ? 'Class' : 'Group' }}: </strong>{{ getGroupName(phrase.groupId) }}<br>
            </div>
            <div v-if="algorithms.length > 1">
              <strong>Recording Type: </strong>{{ getAlgorithm(phrase.algorithm) }}<br>
            </div>
            <wordspotting-list :words="phrase.words" />
            <div
              v-if="isManagerOf(phrase.groupId)"
              class="edit-wordspotting"
            >
              <button
                class="btn clear-btn edit-wordspotting"
                type="submit"
                @click.prevent="formType = 'groups'; algorithm = phrase.algorithm; groupId = phrase.groupId; advanceFrom(READY)"
              >
                Edit Wordspotting
              </button>
              <a
                href="#"
                class="delete-wordspotting"
                @click.prevent="deleteWordspotting('group', phrase.algorithm, phrase.groupId)"
              >
                Reset Wordspotting
              </a>
            </div>
          </div>
        </div>
        <div
          v-if="algorithms.length === 1 && wordspottingGroups.length > 1"
          class="wordspotting-section wordspotting-groups-list"
        >
          <div class="wordspotting-group-name">
            All other {{ (orgType == 'edu') ? 'classes' : 'groups' }} use the following Wordspotting list(s):<br>
          </div>
          <wordspotting-list :words="groupDefaultList" />
        </div>
      </div>

      <!-- User -->
      <div
        v-if="showUserSettings"
        class="wordspotting-section"
        aria-labelledby="manageWordspotting4"
      >
        <h3 id="manageWordspotting4">
          Wordspotting List for Your Account
        </h3>
        <div
          v-for="phrase in wordspotting.user"
          v-if="getAlgorithm(phrase.algorithm)"
          :key="phrase.algorithm + 'user'"
        >
          <div v-if="algorithms.length > 1">
            <strong>Recording Type: </strong>{{ getAlgorithm(phrase.algorithm) }}<br>
          </div>
          <wordspotting-list :words="phrase.words" />
          <div
            v-if="activeWordspotting.indexOf('users') !== -1"
            class="edit-wordspotting"
          >
            <button
              class="btn clear-btn edit-wordspotting"
              type="submit"
              @click.prevent="formType = 'users'; algorithm = phrase.algorithm; groupId = null; advanceFrom(READY)"
            >
              Edit Wordspotting
            </button>
            <a
              href="#"
              class="delete-wordspotting"
              @click.prevent="deleteWordspotting('user', phrase.algorithm)"
            >
              Reset Wordspotting
            </a>
          </div>
        </div>
      </div>

      <div
        v-if="showAddNewWordspotting"
        class="add-new-wordspotting"
      >
        <button
          class="btn btn-primary"
          type="submit"
          @click.prevent="advanceFrom(READY)"
        >
          {{
            addCustomWordspottingButtonLabel
          }}
        </button>
      </div>
    </div>

    <div v-if="formStatus == ORG_GROUP_OR_USER_SELECTION">
      <form @submit.prevent="advanceFrom(ORG_GROUP_OR_USER_SELECTION)">
        Please indicate who the new custom Wordspotting list should apply to.<br><br>
        <label for="formType">
          Apply To:
        </label>
        <select
          id="formType"
          v-model="formType"
          required
        >
          <option
            v-if="activeWordspotting.indexOf('org') !== -1"
            value="org"
          >
            All accounts in {{ orgName }}
          </option>
          <option
            v-if="activeWordspotting.indexOf('groups') !== -1"
            value="groups"
          >
            All accounts in a specific group
          </option>
          <option
            v-if="activeWordspotting.indexOf('users') !== -1"
            value="users"
          >
            Only my account
          </option>
        </select>
        <br><br><br>
        <button
          class="cancelLink btn clear-btn"
          @click.prevent="reset()"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          type="submit"
        >
          Continue
        </button>
      </form>
    </div>


    <div v-if="formStatus == GROUP_SELECTION">
      <form @submit.prevent="advanceFrom(GROUP_SELECTION)">
        Please select which {{ (orgType == 'edu') ? 'class' : 'group' }}
        the new custom Wordspotting list should apply to.<br><br>
        <label for="groupId">
          Select a {{ (orgType == 'edu') ? 'Class' : 'Group' }}:
        </label>
        <select
          id="groupId"
          v-model="groupId"
          class="form-control"
          required
        >
          <option
            v-for="group in wordspottingGroups"
            :value="group.groupId"
          >
            {{ group.name }}
          </option>
        </select>
        <br><br><br>
        <button
          class="cancelLink btn clear-btn"
          @click.prevent="reset()"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          type="submit"
        >
          Continue
        </button>
      </form>
    </div>

    <div v-if="formStatus == ALGORITHM_SELECTION">
      <form @submit.prevent="advanceFrom(ALGORITHM_SELECTION)">
        Please indicate which recording type the new custom Wordspotting config should apply to.
        <br><br>
        <label for="algorithm">
          Select Recording Type:
        </label>
        <select
          id="algorithm"
          v-model="algorithm"
          class="form-control"
          required
        >
          <option
            v-for="algo in algorithms"
            :value="algo.name"
          >
            {{ algo.venue }}
          </option>
        </select>
        <br><br><br>
        <button
          class="cancelLink btn clear-btn"
          @click.prevent="reset()"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          type="submit"
        >
          Continue
        </button>
      </form>
    </div>


    <div v-if="formStatus == WORD_SELECTION">
      <span v-if="formType === 'org'">
        The Wordspotting list which you define below will be used for all &quot;{{ orgName }}&quot; accounts.
      </span>

      <span v-else-if="formType === 'groups'">
        The Wordspotting list which you define below will be used for everyone in the
        &quot;{{ getGroupName(groupId) }}&quot; {{ (orgType == 'edu') ? 'class' : 'group' }}.
      </span>

      <span v-else>
        The Wordspotting list which you define below will be used for all your new recordings.
      </span>

      <br><br>
      Enter the wordspotting phrases you wish to have tracked. Enter 1 phrase in each input box. Each phrase
      can be a maximum of 100 characters long and you can enter up to 25 total phrases.<br><br>
      Clicking on the last empty input box in the list will add a new one beneath it automatically.<br><br>

      <form @submit.prevent="advanceFrom(WORD_SELECTION)">
        <div v-for="(word, index) in words">
          <input
            :id="'word' + index"
            :key="index"
            type="text"
            class="wordspotting-phrase"
            :value="word"
            maxlength="100"
            @click.prevent="phraseClicked"
            @blur.prevent="phraseBlurred"
            @keydown="phraseBlurred"
          >
        </div>
        <br><br><br>
        <button
          class="cancelLink btn clear-btn"
          @click.prevent="reset()"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          type="submit"
        >
          Save
        </button>
      </form>
    </div>
    <div v-if="formStatus == LOADING">
      <h2>
        <svg
          class="icon-refresh rotate large-spinner"
        >
          <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
        </svg>
        Loading...
      </h2>
    </div>
  </section>
</template>

<style lang="less" scoped>
  button.edit-wordspotting {
    margin-right: 25px;
  }
  a.delete-wordspotting {
    position: relative;
    white-space: nowrap;
  }
  .wordspotting-groups-list {
    margin-bottom: 20px;
  }
  .wordspotting-group-name {
    font-size: 14px;
  }
  .edit-wordspotting {
    margin: 8px 4px 12px 4px;
  }
  .wordspotting-section {
    margin: 20px 0 50px 0;
    padding: 8px;
    background-color: #f2f2f2;
    border-radius: 8px;
  }
  #groupId, #algorithm {
    width: 300px;
  }
  .wordspotting-phrase {
    width: 150px;
    margin: 5px;
  }
  .cancelLink {
    margin-right: 50px;
  }
  .add-new-wordspotting {
    margin-top: 25px;
  }
</style>

<script>
import WordspottingList from '../forms/WordspottingList';
import WordspottingMixin from '../pages/WordspottingMixin';
import BasePage from '../pages/BasePage';

export default {
  components: { WordspottingList },
  mixins: [ WordspottingMixin, BasePage ],
  data() {
    return {
      // Constants for adding new wordspotting
      formStatus: 6,
      READY: 1,
      ORG_GROUP_OR_USER_SELECTION: 2,
      GROUP_SELECTION: 3,
      ALGORITHM_SELECTION: 4,
      WORD_SELECTION: 5,
      LOADING: 6,

      formType: null,
      groupId: null,
      algorithm: null,
      words: [],
      wordspotting: null,
    };
  },
  computed: {
    groups() {
      return this.managedGroups;
    }
  },
  methods: {
    basePageCreated() {
      this.$store.dispatch('listWordspotting')
        .then(() => {
          this.wordspotting = this.$store.getters.wordspotting;
          this.formStatus = this.READY;
        });
    },
    // TODO: Make it auto-select for GROUP_SELECTION when there is only 1 managed group?
    advanceFrom(oldStatus) {
      this.$announcer.set('The page has been updated with new content');
      if (oldStatus === this.READY) {
        if (this.formType) {
          // Skip over the next step
          return this.advanceFrom(this.ORG_GROUP_OR_USER_SELECTION);
        } else if (this.activeWordspotting.length >= 2) {
          // The user must specify which type they're adding
          this.formStatus = this.ORG_GROUP_OR_USER_SELECTION;
        } else {
          this.formType = this.activeWordspotting[0];
          // Skip over the next step
          return this.advanceFrom(this.ORG_GROUP_OR_USER_SELECTION);
        }
      } else if (oldStatus === this.ORG_GROUP_OR_USER_SELECTION) {
        if (this.formType === 'groups' && !this.groupId) {
          this.formStatus = this.GROUP_SELECTION;
        } else {
          return this.advanceFrom(this.GROUP_SELECTION);
        }
      } else if (oldStatus === this.GROUP_SELECTION) {
        if (this.algorithm) {
          // Skip over the next step
          return this.advanceFrom(this.ALGORITHM_SELECTION);
        } else if (this.algorithms.length >= 2) {
          // The user must specify which algorithm they're using
          this.formStatus = this.ALGORITHM_SELECTION;
        } else {
          this.algorithm = this.algorithms[0].name;
          // Skip over the next step
          return this.advanceFrom(this.ALGORITHM_SELECTION);
        }
      } else if (oldStatus === this.ALGORITHM_SELECTION) {
        this.loadCurrentWordspotting();

        this.formStatus = this.WORD_SELECTION;
      } else if (oldStatus === this.WORD_SELECTION) {
        // check to make sure the form wasn't reset with the cancel button
        if (!this.algorithm && !this.formType) {
          return false;
        }

        if (!this.trimAndValidateTheWords()) {
          return;
        }

        // Submit the wordspotting
        this.formStatus = this.LOADING;
        const formData = {
          algorithm: this.algorithm,
          words: this.words,
        };

        if (this.formType === 'org') {
          formData.orgId = this.$store.getters.org.orgId;
        } else if (this.formType === 'groups') {
          formData.groupId = this.groupId;
        } else if (this.formType === 'users') {
          formData.userId = this.$store.getters.user.userId;
        }

        this.$store.dispatch('addWordspotting', formData)
          .then(() => {
            this.$announcer.set('Your changes have been saved');
            this.reloadPage();
          });
      }
    },
    // To ensure the page is reactive 100% of the time, we do all this.
    // Maybe 2 $forceUpdate's aren't needed but I was getting frustrated
    reloadPage() {
      this.reset();
      this.$forceUpdate();
      this.$store.dispatch('listWordspotting').then(() => {
        this.wordspotting = this.$store.getters.wordspotting;
        this.$forceUpdate();
      });
    },
    deleteWordspotting(type, algorithm, groupId) {
      if (!window.confirm('Are you sure you wish to remove this wordspotting list and reset to the defaults?')) {
        return false;
      }
      const data = { algorithm };
      if (type === 'org') {
        data.orgId = this.$store.getters.org.orgId;
      } else if (type === 'group') {
        data.groupId = groupId;
      } else if (type === 'user') {
        data.userId = this.$store.getters.user.userId;
      } else {
        alert('Invalid delete request. Please reload the page');
      }

      this.$store.dispatch('deleteWordspotting', data)
        .then(() => this.reloadPage());
    },
    getGroupName(groupId) {
      const group = this._.find(this.$store.getters.groups, {groupId});
      return (group && group.name) ? group.name : 'N/A';
    },
    getAlgorithm(name) {
      const algo = this._.find(this.algorithms, {name});
      return (algo && algo.venue) ? algo.venue : false;
    },
    reset() {
      this.formType = null;
      this.groupId = null;
      this.algorithm = null;
      this.words = [];
      this.formStatus = this.READY;
    }
  }
};
</script>
